/**
 * The Job vocabulary (ADR-0044).
 *
 * A **Job** is one unit of standing work on an Engagement: a trigger, a filter,
 * one act, an Authority, and a status. This module is the closed half of that
 * contract, the part both halves of the product must agree on word for word:
 * the crafts, the event vocabulary, the act list, the trigger shapes, the wire
 * type, and the readiness reasons.
 *
 * Three things stay closed and declared, which is what makes the Job a
 * primitive (the spec's "Extensibility"):
 *
 *   1. **The event vocabulary is the contract.** Nothing dispatches on an ad hoc
 *      string. Adding an event is a named platform change: add it here, in the
 *      spec, and in the catalog's `listens` in the same commit.
 *   2. **Acts are declared, never free.** Every act a Job can perform is in
 *      `JOB_ACTS` with its target and its exclusive-or-advisory nature.
 *   3. **Platform adds kinds; customers add instances.** `JOB_KIND_IDS` is the
 *      platform's list; the long tail is the `<craft>.custom` kind.
 *
 * Copy rules apply to every `label` in here, because every label reaches a
 * surface: no em-dashes, and a Job is always possessed in copy ("Bailey's
 * jobs"). The kind id is a stable identifier for code, God Mode, and the
 * OpenTelemetry contract (ADR-0039); it never appears in customer copy.
 */
import { WRITTEN_AS, } from "./harperApproach.js";
/**
 * The crafts a Job kind hangs off. The catalog is keyed by CRAFT, not by Agent,
 * so a workspace that renames Bailey, or engages its own Agent of craft
 * `builder`, gets the same Jobs. One Agent per craft is a product rule
 * (enforced in the backend registry); craft is internal vocabulary and shows to
 * a customer only as the line under an Agent's name.
 */
export const JOB_CRAFTS = ["pm", "staff", "builder", "reviewer", "qa", "chronicler"];
/**
 * The closed event vocabulary. A trigger of type `event` names one of these and
 * nothing else.
 *
 * Nineteen events in three groups: the work-item transitions the lifecycle and
 * the gate resolvers emit, the source events the forge, deploy and tracker
 * ingresses emit, and the two question events the planning run emits.
 *
 * Reconciled 2026-09-14 (PR 1) as the union of the spec's "Extensibility" list
 * and its catalog table. `plan.drafted` and `proposal.minted` retired with
 * Reeve's critique lines (ADR-0049): nothing listens for either.
 */
export const JOB_EVENTS = [
    // Work-item transitions (`agent/runs/lifecycle.ts` and the gate resolvers).
    "item.accepted",
    "plan.approved",
    "build.opened",
    "review.posted",
    "review.changes_requested",
    "qa.passed",
    "qa.failed",
    "item.done",
    // Source events (the pull request webhook, the QA ingress, the deployment
    // webhook).
    "pr.opened",
    "pr.updated",
    "pr.ready",
    "pr.approved_and_verified",
    // Every signal on a Wright pull request head is in (CI, review, QA,
    // mergeability), or its deadline passed: one fix round answers all of it.
    "pr.signals_settled",
    "deploy.staging",
    "deploy.production",
    "release.candidate",
    // A tracker issue was assigned to yagni (Linear's agent session, Jira's
    // Rovo agent). The subject carries a `TrackerAssignmentSubject` in `extra`
    // (backend `jobs/types.ts`) and the emit's `sourceRef` is
    // `<source>:<assignment id>`. Not a pull request event: it never joins
    // `JOB_PULL_REQUEST_EVENTS`.
    "tracker.assigned",
    // Questions (the planning run, and an answer from a person or a routing line).
    "question.raised",
    "question.answered",
];
export function isJobEvent(value) {
    return typeof value === "string" && JOB_EVENTS.includes(value);
}
/**
 * The causes a run can carry that are not a listened-for event. A run is always
 * caused by something, and "the clock came round", "a person asked", and "the
 * queue had room" are causes with no event behind them.
 */
export const JOB_CAUSE_EVENTS = ["clock.tick", "on_demand", "queue", "catch_up"];
/**
 * Every act a Job can perform, the spec's catalog table plus the two reserved
 * step acts. A kind names one of these or is hard-wired with none.
 *
 * `hold` and `release` are reserved (spec "Extensibility" §2): a Job whose act
 * is hold-or-release, triggered on a transition, IS a custom step. Nothing
 * dispatches them in this run; the catalog interface holds them so Regis's
 * post-QA step is a row and not a schema change.
 */
export const JOB_ACTS = [
    {
        id: "accept_proposal",
        target: "source",
        nature: "exclusive",
        label: "accept the top proposal into Plan",
    },
    {
        id: "route_question",
        target: "person",
        nature: "advisory",
        label: "post and tag the named answerer",
    },
    { id: "send_back", target: "source", nature: "advisory", label: "send back" },
    {
        id: "request_changes",
        target: "source",
        nature: "advisory",
        label: "request changes",
    },
    {
        id: "proceed_to_build",
        target: "source",
        nature: "exclusive",
        label: "proceed to build without a person approving",
    },
    {
        id: "open_pr",
        target: "source",
        nature: "exclusive",
        label: "open PR ready for review, not draft",
    },
    { id: "push_fix", target: "source", nature: "exclusive", label: "push the fix" },
    { id: "merge", target: "source", nature: "exclusive", label: "merge" },
    { id: "approve_pr", target: "source", nature: "exclusive", label: "approve" },
    {
        id: "publish_verdict",
        target: "source",
        nature: "exclusive",
        label: "publish evidence and verdict",
    },
    {
        id: "publish_sign_off",
        target: "source",
        nature: "exclusive",
        label: "publish sign-off",
    },
    {
        id: "publish_brief",
        target: "source",
        nature: "exclusive",
        label: "publish the in-app Brief",
    },
    {
        id: "post_work_log",
        target: "source",
        nature: "exclusive",
        label: "post to delivery target",
    },
    {
        id: "post_deploy_notes",
        target: "source",
        nature: "exclusive",
        label: "post the notes",
    },
    { id: "post", target: "source", nature: "advisory", label: "post" },
    /**
     * Move a repository's reviews to this Team's review line (plan
     * 2026-09-16, decision 14). One Team reviews a repository; on every other
     * Team that attaches it the line says who does, and this is the one click
     * that changes the answer.
     *
     * It is a PERSON's act on the line, so no kind names it, it mints no gate,
     * and it has no Authority rung: an Agent never performs it, which is why
     * there is nothing here for a ladder to bound.
     */
    {
        id: "reviewer.claim_repo",
        target: "source",
        nature: "exclusive",
        label: "move this repository's reviews here",
    },
    {
        id: "hold",
        target: "source",
        nature: "exclusive",
        label: "keep the item in its current stage with a named reason",
    },
    { id: "release", target: "source", nature: "exclusive", label: "let it move" },
];
export const JOB_ACT_IDS = JOB_ACTS.map((act) => act.id);
export function getJobAct(id) {
    // Total by construction: `JobActId` is the list's own id union.
    return JOB_ACTS.find((act) => act.id === id);
}
/** What starts a Job. "Rhythm" is the customer word for `clock` and nothing else. */
export const JOB_TRIGGER_TYPES = ["clock", "event", "queue", "on_demand"];
/**
 * The ladder, two rungs (ADR-0044 §3). Training is retired: at Supervised a
 * person who does not click has an advisory review beside an incumbent. There
 * is no demotion and no graduation suggestion; promotion is a person's explicit
 * act on the line.
 *
 * `null` on a Job means hard-wired: no act, so no ladder.
 */
export const JOB_AUTHORITIES = ["supervised", "autonomous"];
/**
 * How hard a Team's Proctor reviews, stored on the review line's
 * `filter.reviewLevel`. Light posts only risks and approves when none is
 * left; Balanced is the review as it always was; Tough approves only when
 * nothing above a nit is left. An absent value is Balanced.
 */
export const REVIEW_LEVELS = ["light", "balanced", "tough"];
export const DEFAULT_REVIEW_LEVEL = "balanced";
export function isReviewLevel(value) {
    return typeof value === "string" && REVIEW_LEVELS.includes(value);
}
/**
 * Every kind the platform ships, `<craft>.<verb>`. A customer never authors a
 * kind; the long tail is `<craft>.custom`.
 */
export const JOB_KIND_IDS = [
    "pm.propose",
    "pm.take_assigned",
    "pm.route_question",
    "staff.plan",
    "builder.build",
    "builder.fix_round",
    "builder.merge",
    "reviewer.review_pr",
    "qa.verify_pr",
    "qa.sign_off_release",
    "chronicler.brief",
    "chronicler.work_log",
    "chronicler.deploy_notes",
    "chronicler.custom",
];
export function isJobKindId(value) {
    return typeof value === "string" && JOB_KIND_IDS.includes(value);
}
/** The instance name every single-instance kind carries. */
export const DEFAULT_JOB_INSTANCE = "default";
/** The entry in {@link TeamTicketOwners} for tickets nobody holds. */
export const TICKET_OWNERS_UNASSIGNED = "unassigned";
/** How a `chronicler.custom` line writes: its **Written as** (`harperApproach.ts`). */
export const JOB_BODY_CATEGORIES = WRITTEN_AS;
/** The one kind whose line carries a `harper_jobs` body (ADR-0044 decision 2). */
export const BODY_BACKED_JOB_KIND = "chronicler.custom";
/**
 * A body-backed line with no body behind it (`filter.harperJobId` unset): it
 * has nothing to post, so it is never switched on or run (the server refuses
 * both) and reads "needs setup: nothing to post yet" if it is already on.
 */
export function lineHasNoJob(job) {
    if (job.kind !== BODY_BACKED_JOB_KIND)
        return false;
    const filter = job.filter;
    if (filter === null || typeof filter !== "object" || Array.isArray(filter))
        return true;
    const id = filter.harperJobId;
    return !(typeof id === "string" && id.trim());
}
/**
 * The reserved `default` line of a body-backed kind, off and with nothing
 * behind it. Every Team is seeded with one, and it can do nothing: Harper's
 * lines are added by name. The Team page and Agents leave it out; its own
 * URL still opens.
 */
export function isReservedEmptyLine(job) {
    return job.instance === DEFAULT_JOB_INSTANCE && !job.active && lineHasNoJob(job);
}
const REVISION_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
/**
 * The repository environment revision a `qa.verify_pr` line narrows to,
 * lowercased, or null when it verifies every pull request in the
 * repository's environment.
 *
 * `pr.ready` names the repository's active QA environment REVISION id, and a
 * pull request is always verified against that revision. Only a revision id
 * narrows. A deploy-side name ("staging", "production"), which the Team
 * page's picker offered before this blank was fixed and which lines saved
 * then still carry, means nothing at PR time: it reads as empty, so such a
 * line verifies every pull request instead of never running. The match, the
 * save, the line status and both sentence renderers read it from here.
 */
export function qaVerifyRevisionOf(filter) {
    if (!filter || typeof filter !== "object")
        return null;
    const environment = filter.environment;
    if (typeof environment !== "string")
        return null;
    const value = environment.trim().toLowerCase();
    return REVISION_ID.test(value) ? value : null;
}
/**
 * The status word on the line. Derived, never stored.
 *
 * `off` is the Job's own switch; `paused` is the Team's brake, which stops every
 * Job atomically; `needs_setup` carries one reason from the closed list below.
 * A switch read as "enable the agent" and hid the state that matters most,
 * enabled but producing nothing, so the line shows a word (design review
 * 2026-09-14).
 */
export const JOB_READINESS = ["on", "off", "paused", "needs_setup"];
/**
 * Why a Job is not ready, closed list, in the spec's order. The `phrase` is the
 * customer copy that follows "needs setup: ".
 */
export const JOB_READINESS_REASONS = [
    // A Harper line with no job behind it (no instructions): it would skip every
    // firing. First because nothing below it can make the line post.
    { id: "no_job_behind_this_line", phrase: "nothing to post yet" },
    { id: "no_repository_attached", phrase: "no repository attached" },
    { id: "no_backlog_attached", phrase: "no backlog attached" },
    { id: "no_repository_environment", phrase: "no repository environment" },
    { id: "no_delivery_target", phrase: "no delivery target" },
    { id: "no_answerers_named", phrase: "no answerers named" },
    { id: "no_supervisor_member", phrase: "no supervisor who is a member" },
    { id: "slack_not_connected", phrase: "Slack not connected" },
    {
        id: "github_app_not_installed",
        phrase: "GitHub app not installed on the repository",
    },
];
export const JOB_READINESS_REASON_IDS = JOB_READINESS_REASONS.map((reason) => reason.id);
export function jobReadinessPhrase(reason) {
    return JOB_READINESS_REASONS.find((entry) => entry.id === reason)?.phrase ?? reason;
}
/**
 * Why a Job stopped: the category of the last failed run. `gate` is not a
 * failure, it is a Job waiting on a person who has not clicked.
 */
export const JOB_STOPPED_REASONS = ["infrastructure", "provider", "gate"];
/**
 * Whether an on line kept posting (ADR-0046, amended 2026-09-28). `missed`:
 * its last scheduled run did not post and nothing has caught it up yet.
 * `held`: it is waiting on capacity and runs on its own once a person adds it.
 * Only Harper's lines keep this ledger today.
 */
export const JOB_CONTINUITY_WORDS = ["missed", "held"];
//# sourceMappingURL=jobs.js.map